import { useEffect, useState } from "react";
import { Plus, MoreVertical, Save, X, Trash2 } from "lucide-react";
import FinanceLayout from "@/components/FinanceLayout";
import { useWorkspace } from "@/context/WorkspaceContext";
import { api, formatApiError } from "@/lib/api";
import { fmtTL, fmtDateTR } from "@/lib/finance";
import { toast } from "sonner";

const initials = (n) => (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
const AVATAR_COLORS = ["bg-emerald-100 text-emerald-700", "bg-orange-100 text-orange-700", "bg-sky-100 text-sky-700", "bg-red-100 text-red-700", "bg-purple-100 text-purple-700", "bg-amber-100 text-amber-700"];
const colorFor = (n) => AVATAR_COLORS[(n?.charCodeAt(0) || 0) % AVATAR_COLORS.length];

export default function Personnel() {
  const { workspace } = useWorkspace();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ full_name: "", title: "", salary: "", payment_date: "", add_to_recurring_expenses: true });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [openMenu, setOpenMenu] = useState(null);

  const load = async () => {
    const { data } = await api.get("/finance/personnel", { params: { workspace } });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [workspace]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.full_name.trim()) return setError("Ad Soyad zorunlu.");
    setSaving(true);
    try {
      await api.post("/finance/personnel", {
        workspace, full_name: f.full_name, title: f.title,
        salary: parseFloat(f.salary || 0), payment_date: f.payment_date || null,
        add_to_recurring_expenses: f.add_to_recurring_expenses,
      });
      toast.success("Personel eklendi");
      setOpen(false);
      setF({ full_name: "", title: "", salary: "", payment_date: "", add_to_recurring_expenses: true });
      load();
    } catch (err) { setError(formatApiError(err.response?.data?.detail)); } finally { setSaving(false); }
  };

  const del = async (id) => {
    await api.delete(`/finance/personnel/${id}`);
    toast.success("Personel silindi");
    setOpenMenu(null);
    load();
  };

  const input = "w-full h-11 rounded-lg border border-slate-200 bg-white px-4 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-100 focus:border-emerald-500";

  return (
    <FinanceLayout>
      <div className="mb-8 flex items-end justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-slate-900">Personel</h1>
          <p className="text-sm text-slate-500 mt-2">Ekibinizdeki personel bilgilerini görüntüleyin ve yönetin.</p>
        </div>
        <button onClick={() => setOpen(true)} data-testid="pers-new-btn" className="rounded-lg bg-emerald-800 hover:bg-emerald-700 text-white px-5 py-2.5 text-sm font-semibold inline-flex items-center gap-2">
          <Plus size={15} /> Yeni Personel Ekle
        </button>
      </div>

      <div className="rounded-2xl border border-slate-100 bg-white overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 bg-slate-50/40">
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ad Soyad</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ünvan</th>
              <th className="text-right py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Maaş</th>
              <th className="text-left py-4 px-6 text-xs font-semibold uppercase tracking-wider text-slate-500">Ödeme Tarihi</th>
              <th className="w-16"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 ? (
              <tr><td colSpan={5} className="py-14 text-center text-sm text-slate-500" data-testid="pers-empty">Henüz personel eklenmemiş.</td></tr>
            ) : items.map((p) => (
              <tr key={p.id} className="border-b border-slate-50 last:border-none hover:bg-slate-50/40" data-testid={`pers-row-${p.id}`}>
                <td className="py-4 px-6"><div className="flex items-center gap-3">
                  <div className={`w-10 h-10 rounded-full ${colorFor(p.full_name)} flex items-center justify-center font-semibold text-xs`}>{initials(p.full_name)}</div>
                  <span className="text-sm font-medium text-slate-900">{p.full_name}</span>
                </div></td>
                <td className="py-4 px-6 text-sm text-slate-700">{p.title || "—"}</td>
                <td className="py-4 px-6 text-sm font-semibold text-slate-900 text-right">{fmtTL(p.salary)}</td>
                <td className="py-4 px-6 text-sm text-slate-700">{p.payment_date ? fmtDateTR(p.payment_date) : "—"}</td>
                <td className="py-4 px-6 relative">
                  <button onClick={() => setOpenMenu(openMenu === p.id ? null : p.id)} className="text-slate-400 hover:text-slate-700 p-1" data-testid={`pers-menu-${p.id}`}><MoreVertical size={16} /></button>
                  {openMenu === p.id && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setOpenMenu(null)} />
                      <div className="absolute right-6 top-full z-20 rounded-md border border-slate-200 bg-white shadow-md py-1 min-w-[140px]">
                        <button onClick={() => del(p.id)} data-testid={`pers-del-${p.id}`} className="w-full text-left px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 flex items-center gap-2">
                          <Trash2 size={13} /> Sil
                        </button>
                      </div>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="px-6 py-3 border-t border-slate-100 bg-slate-50/40 text-xs text-slate-500" data-testid="pers-count">Toplam {items.length} personel</div>
      </div>

      {open && (
        <div className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center px-6" data-testid="pers-modal">
          <div className="bg-white rounded-2xl max-w-[480px] w-full">
            <div className="flex items-center justify-between px-6 py-5 border-b border-slate-100">
              <h3 className="text-lg font-semibold text-slate-900">Yeni Personel</h3>
              <button onClick={() => setOpen(false)} className="text-slate-400 hover:text-slate-900" data-testid="pers-close"><X size={18} /></button>
            </div>
            <form onSubmit={submit} className="p-6 space-y-4">
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Ad Soyad</label>
                <input value={f.full_name} onChange={(e) => setF({ ...f, full_name: e.target.value })} className={input} placeholder="Antonio Samuel" data-testid="pers-name" /></div>
              <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Ünvan</label>
                <input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} className={input} placeholder="Ajans Direktörü" data-testid="pers-title" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Maaş (₺)</label>
                  <input type="number" step="0.01" value={f.salary} onChange={(e) => setF({ ...f, salary: e.target.value })} className={input} placeholder="30000" data-testid="pers-salary" /></div>
                <div><label className="block text-sm font-medium text-slate-700 mb-1.5">Ödeme Tarihi</label>
                  <input type="date" value={f.payment_date} onChange={(e) => setF({ ...f, payment_date: e.target.value })} className={input} data-testid="pers-paydate" /></div>
              </div>
              <label className="flex items-center gap-2 text-sm text-slate-700 pt-2">
                <input type="checkbox" checked={f.add_to_recurring_expenses} onChange={(e) => setF({ ...f, add_to_recurring_expenses: e.target.checked })} data-testid="pers-add-exp" className="w-4 h-4 accent-emerald-600" />
                Maaş otomatik olarak Sürekli Gider'e eklensin
              </label>
              {error && <div className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2" data-testid="pers-error">{error}</div>}
              <div className="flex justify-end gap-2 pt-3">
                <button type="button" onClick={() => setOpen(false)} className="rounded-full border border-slate-200 bg-white text-slate-700 hover:bg-slate-50 px-5 py-2 text-sm font-medium">İptal</button>
                <button type="submit" disabled={saving} data-testid="pers-save" className="rounded-full bg-emerald-800 hover:bg-emerald-700 text-white px-6 py-2 text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50">
                  <Save size={13} /> {saving ? "Kaydediliyor..." : "Kaydet"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </FinanceLayout>
  );
}
